import { ArrowRight, BookOpen, ClipboardList, Compass, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/locale-link";
import { track } from "@/lib/analytics";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import {
  ATLAS_ROUTE,
  CONSUMER_JOIN_ROUTE,
  HUMIDOR_ROUTE,
  REGISTRY_ROUTE,
} from "@/lib/homepage-ctas";
import { useJournalCta } from "@/hooks/useJournalCta";

/**
 * Consumer capability cards — "Discover. Record. Reflect. Share."
 *
 * Shared by the desktop homepage (HomeV2) and the signed-out mobile homepage
 * so the Cigar Journey and Cigar Journal narrative stays identical on both.
 * Copy describes only shipped capability: Registry, Journal, Humidor, Atlas.
 */
export function CapabilityCards({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const journalCta = useJournalCta();

  const cards: Array<{
    icon: typeof BookOpen;
    title: string;
    body: string;
    close: string;
    note?: string;
    cta: string;
    to: string;
    key: string;
    highlight?: boolean;
    onSelect?: () => void;
  }> = [
    {
      icon: BookOpen,
      title: t("homeCap.registryTitle"),
      body: t("homeCap.registryBody"),
      close: t("homeCap.registryClose"),
      cta: t("homeCap.registryCta"),
      to: REGISTRY_ROUTE,
      key: "registry",
    },
    {
      icon: ClipboardList,
      title: t("homeCap.journalTitle"),
      body: t("homeCap.journalBody"),
      close: t("homeCap.journalClose"),
      note: t("homeCap.journalPrivacy"),
      cta: t("homeCap.journalCta"),
      to: journalCta.to,
      onSelect: journalCta.onClick,
      key: "journal",
      highlight: true,
    },
    {
      icon: Package,
      title: t("homeCap.humidorTitle"),
      body: t("homeCap.humidorBody"),
      close: t("homeCap.humidorClose"),
      cta: t("homeCap.humidorCta"),
      to: user ? HUMIDOR_ROUTE : CONSUMER_JOIN_ROUTE,
      key: "humidor",
    },
    {
      icon: Compass,
      title: t("homeCap.exploreTitle"),
      body: t("homeCap.exploreBody"),
      close: t("homeCap.exploreClose"),
      cta: t("homeCap.exploreCta"),
      to: ATLAS_ROUTE,
      key: "explore",
    },
  ];

  return (
    <section className={compact ? "py-10 bg-background" : "py-12 md:py-20 bg-background"}>
      <div className="container">
        <div className="max-w-2xl">
          <span className="editorial-subheading text-primary tracking-[0.2em] text-[11px]">
            {t("homeCap.eyebrow")}
          </span>
          <h2 className="font-display text-[22px] md:text-4xl font-semibold mt-2 leading-tight">
            {t("homeCap.heading")}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mt-3 leading-relaxed">
            {t("homeCap.subheading")}
          </p>
        </div>

        <div
          className={
            compact
              ? "mt-6 flex flex-col gap-3"
              : "mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          }
        >
          {cards.map((card) => (
            <div
              key={card.key}
              className={`rounded-xl md:rounded-2xl border bg-card p-5 md:p-6 flex flex-col ${
                card.highlight ? "border-primary/40" : "border-border/60"
              }`}
            >
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <card.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-base md:text-lg font-semibold mt-4 leading-snug">
                {card.title}
              </h3>
              <p className="text-[13px] md:text-sm text-muted-foreground mt-2 leading-relaxed">
                {card.body}
              </p>
              <p className="text-[13px] md:text-sm text-foreground/80 mt-2 leading-relaxed">
                {card.close}
              </p>
              {card.note && (
                <p className="text-xs text-muted-foreground/80 mt-2 leading-relaxed">{card.note}</p>
              )}
              <Link
                to={card.to}
                className="mt-4 md:mt-auto md:pt-4 inline-block"
                onClick={() => {
                  card.onSelect?.();
                  track("audience_cta_clicked", {
                    audience: "consumer",
                    intent: card.key,
                    destination: card.to,
                    surface: compact ? "home_mobile_capabilities" : "home_desktop_capabilities",
                    device: compact ? "mobile" : "desktop",
                  });
                }}
              >
                <Button
                  variant={card.highlight ? "default" : "outline"}
                  className="w-full min-h-[44px] gap-2 font-medium"
                >
                  {card.cta} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
